import { useState } from "react";
import { Eye, EyeOff, Phone, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AppDialog } from "@/components/app/AppDialog";
import { AppButton } from "@/components/app/AppButton";
import { useApp } from "@/context/AppContext";
import { ROUTES } from "@/lib/routes";

interface LoginModalProps {
  open: boolean;
  onClose: () => void;
}

export function LoginModal({ open, onClose }: LoginModalProps) {
  const { login } = useApp();
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = () => {
    setError("");
    if (!phone || !password) {
      setError("Please enter phone number and password.");
      return;
    }
    if (phone !== "9898989898") {
      setError("Invalid phone number.");
      return;
    }
    if (password !== "Admin@123") {
      setError("Invalid password.");
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      login();
      setPhone("");
      setPassword("");
      setError("");
      onClose();
      navigate(ROUTES.PRODUCTS);
    }, 800);
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={(v) => !v && onClose()}
      size="sm"
      title="Welcome Back"
    >
      <div className="space-y-5">
        <p className="text-sm text-muted-foreground text-center -mt-2">
          Sign in to your Smora account
        </p>

        {/* Phone Field */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">
            Phone Number
          </label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="tel"
              placeholder="Enter your phone number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              maxLength={10}
              className="w-full pl-10 pr-4 py-2.5 border border-border rounded-xl bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition"
            />
          </div>
        </div>

        {/* Password Field */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">
            Password
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 border border-border rounded-xl bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <p className="text-sm text-destructive text-center">{error}</p>
        )}

        {/* Login Button */}
        <AppButton
          size="lg"
          fullWidth
          loading={loading}
          onClick={handleLogin}
        >
          Login
        </AppButton>
      </div>
    </AppDialog>
  );
}
